import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Eye, ArrowDown, Plus } from 'lucide-react';
import { useTradingAccounts } from '@/api/trading-accounts/use-trading-accounts';
import MobileFloatingNav from '@/components/mobile/MobileFloatingNav';
import { formatCurrencyAmount } from '@/utils/formatters';
import { Loader2 } from 'lucide-react';

const MobileAccountsPage: React.FC = () => {
    const { t } = useTranslation('mobile');
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<'live' | 'demo'>('live');
    const { data: tradingAccounts, isLoading } = useTradingAccounts({
        refetchInterval: 15000, // Reduced from 1s — balance events not on WebSocket yet
    });

    if (isLoading) {
        return (
            <div className="min-h-screen text-foreground flex items-center justify-center pb-20">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    // Filter accounts by type (live vs demo)
    const liveAccounts = tradingAccounts?.accounts?.filter(acc => !acc.is_demo) || [];
    const demoAccounts = tradingAccounts?.accounts?.filter(acc => acc.is_demo) || [];
    const accounts = activeTab === 'live' ? liveAccounts : demoAccounts;

    // Calculate total balance
    const totalBalance = accounts.reduce((sum, acc) => sum + Number(acc.equity || 0), 0);

    const getCurrencySymbol = (currency: string) => {
        const symbols: Record<string, string> = {
            USD: '$',
            EUR: '€',
            GBP: '£',
            JPY: '¥',
        };
        return symbols[currency] || currency;
    };

    const getAccountColor = (currency: string) => {
        const colors: Record<string, string> = {
            USD: 'bg-emerald-600',
            EUR: 'bg-indigo-600',
            GBP: 'bg-purple-600',
            JPY: 'bg-rose-600',
        };
        return colors[currency] || 'bg-slate-600';
    };

    return (
        <div className="min-h-screen text-foreground pb-32">
            {/* Header Tabs */}
            <div className="flex gap-2 px-4 pt-6">
                <button
                    onClick={() => setActiveTab('live')}
                    className={`flex-1 py-3 text-sm font-medium rounded-2xl transition-colors ${
                        activeTab === 'live'
                            ? 'text-white trader-fab-gradient'
                            : 'text-muted-foreground trader-glass'
                    }`}
                >
                    {t('liveAccounts')}
                </button>
                <button
                    onClick={() => setActiveTab('demo')}
                    className={`flex-1 py-3 text-sm font-medium rounded-2xl transition-colors ${
                        activeTab === 'demo'
                            ? 'text-white trader-fab-gradient'
                            : 'text-muted-foreground trader-glass'
                    }`}
                >
                    {t('demoAccounts')}
                </button>
            </div>

            {/* Total Balance Card */}
            <div className="px-4 pt-6">
                <div className="trader-glass rounded-3xl p-6 mb-6">
                    <div className="flex items-center justify-between mb-4">
                        <span className="text-muted-foreground">{t('totalBalanceUsd')}</span>
                        <Eye className="w-5 h-5 text-muted-foreground" />
                    </div>
                    <div className="text-3xl font-bold mb-6 tracking-tight">
                        {formatCurrencyAmount(totalBalance, 'USD')}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-3">
                        <button
                            onClick={() => navigate('/deposit-withdraw?action=deposit')}
                            className="flex-1 py-3 trader-fab-gradient text-white hover:opacity-90 rounded-2xl flex items-center justify-center gap-2 transition-opacity"
                        >
                            <ArrowDown className="w-5 h-5" />
                            <span className="font-medium">{t('deposit')}</span>
                        </button>
                    </div>
                </div>

                {/* Accounts Header */}
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-semibold">{t('myAccounts')}</h2>
                    {accounts.length < 2 && (
                        <button className="flex items-center gap-1 text-primary text-sm">
                            <Plus className="w-4 h-4" />
                            <span>{t('newAccount')}</span>
                        </button>
                    )}
                </div>

                {/* Accounts List */}
                <div className="space-y-3">
                    {accounts.map(account => {
                        const pl = Number(account.daily_pnl || 0);
                        const plPositive = pl >= 0;
                        const currencySymbol = getCurrencySymbol(account.base_currency);
                        const accountColor = getAccountColor(account.base_currency);

                        return (
                            <div
                                key={account.id}
                                className="trader-glass rounded-2xl p-4 hover:bg-foreground/5 transition-colors cursor-pointer"
                                onClick={() => navigate(`/accounts/${account.id}`)}
                            >
                                <div className="flex items-start justify-between mb-3">
                                    <div className="flex items-center gap-3">
                                        <div
                                            className={`w-12 h-12 ${accountColor} rounded-xl flex items-center justify-center text-2xl font-bold`}
                                        >
                                            {currencySymbol}
                                        </div>
                                        <div>
                                            <div className="font-semibold text-lg">
                                                {account.base_currency} {t('tradingAccount')}
                                            </div>
                                            <div className="text-xs text-muted-foreground">
                                                {t('acc')}: {account.account_number || account.id}
                                            </div>
                                        </div>
                                    </div>
                                    <div
                                        className={`px-2 py-1 rounded text-xs font-medium ${
                                            plPositive
                                                ? 'trader-pill-positive'
                                                : 'trader-pill-negative'
                                        }`}
                                    >
                                        {plPositive ? '+' : ''}
                                        {formatCurrencyAmount(pl, account.base_currency)}
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4 pt-3 border-t border-border">
                                    <div>
                                        <div className="text-xs text-muted-foreground mb-1">
                                            {t('balance')}
                                        </div>
                                        <div className="font-semibold">
                                            {formatCurrencyAmount(
                                                account.balance,
                                                account.base_currency
                                            )}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-xs text-muted-foreground mb-1">
                                            {t('equity')}
                                        </div>
                                        <div className="font-semibold">
                                            {formatCurrencyAmount(
                                                account.equity,
                                                account.base_currency
                                            )}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-xs text-muted-foreground mb-1">
                                            {t('currency')}
                                        </div>
                                        <div className="font-semibold">{account.base_currency}</div>
                                    </div>
                                    <div>
                                        <div className="text-xs text-muted-foreground mb-1">
                                            {t('leverage')}
                                        </div>
                                        <div className="font-semibold">1:{account.leverage}</div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            <MobileFloatingNav />
        </div>
    );
};

export default MobileAccountsPage;
